import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { BarChart3, Check, FolderTree, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { groupByCategory, useDocuments, useSignatureReport, type Document } from '../api';
import { useDocCategories, useDocumentMutations } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * ניהול נהלים (PRD 5.10.1).
 *
 * המסך מחולק לקטגוריות באותו סדר שהעובד רואה אותן, כי מי שכותב
 * נוהל צריך לראות איך הוא ייראה אחר כך. נוהל שמשויך לשתיים מופיע
 * בשתיהן.
 *
 * עדכון תוכן מעלה גרסה אוטומטית בשרת ומבקש חתימה מחדש, ולכן המסך
 * אומר את זה לפני השמירה: זו החלטה עם תוצאה.
 */
export function DocumentsAdminPage() {
  const confirm = useConfirm();
  const canManage = usePerm('docs.manage');
  // גם מה שלא פורסם. בלי זה נוהל בכתיבה נעלם ממסך הניהול עצמו.
  const documents = useDocuments(true);
  const categories = useDocCategories();
  const mutations = useDocumentMutations();

  const [editing, setEditing] = useState<Document | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [openReport, setOpenReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const askRemove = (document: Document) =>
    confirm.ask({
      title: 'מחיקת נוהל',
      body: (
        <>
          <p className="m-0">"{document.title}"</p>
          <p className="m-0 text-body-sm text-muted">
            החתימות שעובדים כבר חתמו עליו יימחקו איתו, ולא תישאר ראיה שהם קראו אותו.
          </p>
        </>
      ),
      confirmLabel: 'מחיקת הנוהל והחתימות',
      onConfirm: () => run(() => mutations.remove.mutateAsync(document.id)),
    });

  const groups = groupByCategory(documents.data ?? []);

  return (
    <AdminPage
      title="נהלים"
      subtitle="מה כתוב, ומי חתם"
      actions={
        !isAdding &&
        !editing && (
          <div className="flex flex-wrap gap-2">
            {/*
              הקטגוריות הן טבלה עם מסך ניהול (כלל 1), והקישור יושב
              כאן ולא רק בהגדרות: מי שמגלה שחסרה לו קטגוריה מגלה
              את זה בדיוק ברגע שהוא כותב נוהל.
            */}
            <Link to="/admin/documents/categories" className="no-underline">
              <Button variant="secondary">
                <FolderTree size={18} strokeWidth={1.75} aria-hidden="true" />
                קטגוריות
              </Button>
            </Link>
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              נוהל חדש
            </Button>
          </div>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {(isAdding || editing) && (
        <Panel title={editing ? `עריכת ${editing.title}` : 'נוהל חדש'}>
          <DocumentForm
            key={editing?.id ?? 'new'}
            document={editing}
            categories={categories.data ?? []}
            saving={mutations.save.isPending}
            onCancel={() => {
              setEditing(null);
              setIsAdding(false);
            }}
            onSave={(input) =>
              run(async () => {
                await mutations.save.mutateAsync({ id: editing?.id, ...input });
                setEditing(null);
                setIsAdding(false);
              })
            }
          />
        </Panel>
      )}

      {documents.isPending && <Skeleton className="h-40" />}
      {documents.isError && <ErrorState onRetry={() => void documents.refetch()} />}

      {documents.isSuccess && groups.length === 0 && !isAdding && (
        <EmptyState title="אין נהלים" description="נוהל הוא מה שכל עובד צריך לדעת ולפעול לפיו." />
      )}

      {groups.map((group) => (
        <Panel key={group.id ?? 'loose'} title={`${group.name} (${group.documents.length})`}>
          <Table>
            <thead>
              <Tr>
                <Th>כותרת</Th>
                <Th>גם ב</Th>
                <Th>גרסה</Th>
                <Th>עודכן</Th>
                <Th>מצב</Th>
                <Th>
                  <span className="sr-only">פעולות</span>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {group.documents.map((document) => (
                <Tr key={document.id}>
                  <Td>{document.title}</Td>
                  {/*
                    רק הקטגוריות האחרות. הנוכחית היא כותרת הבלוק,
                    וחזרה עליה בכל שורה היתה רעש.
                  */}
                  <Td>
                    {document.categories.filter((category) => category.id !== group.id).length ===
                    0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {document.categories
                          .filter((category) => category.id !== group.id)
                          .map((category) => (
                            // שם קטגוריה אינו סטטוס, ולכן תג נייטרלי
                            // ולא StatusBadge בצבע
                            <span
                              key={category.id}
                              className="rounded-pill border border-line px-2 py-0.5 text-label text-muted"
                            >
                              {category.name}
                            </span>
                          ))}
                      </div>
                    )}
                  </Td>
                  <Td className="tabular">{document.version}</Td>
                  <Td className="tabular">{formatDate(document.updated_at)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {!document.is_published && <StatusBadge tone="bad">לא פורסם</StatusBadge>}
                      {document.requires_signature && (
                        <StatusBadge tone="warn">נדרשת חתימה</StatusBadge>
                      )}
                      {document.is_published && !document.requires_signature && (
                        <span className="text-muted">מפורסם</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      {document.requires_signature && (
                        <Button
                          variant="ghost"
                          aria-label={`מעקב חתימות על ${document.title}`}
                          onClick={() =>
                            setOpenReport(openReport === document.id ? null : document.id)
                          }
                        >
                          <BarChart3 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        aria-label={`עריכת ${document.title}`}
                        onClick={() => {
                          setEditing(document);
                          setIsAdding(false);
                        }}
                      >
                        <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`מחיקת ${document.title}`}
                        onClick={() => askRemove(document)}
                      >
                        <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      ))}

      {openReport && <SignatureReport documentId={openReport} />}
      {confirm.dialog}
    </AdminPage>
  );
}

function SignatureReport({ documentId }: { documentId: string }) {
  const report = useSignatureReport(documentId);

  if (report.isPending) return <Skeleton className="h-32" />;
  if (!report.data) return null;

  const data = report.data;

  return (
    <Panel title={`מי חתם על גרסה ${data.version}`}>
      <div className="grid gap-4">
        <Progress
          value={data.signed}
          max={data.total}
          label={`${data.signed} מתוך ${data.total} חתמו`}
        />

        <Table>
          <thead>
            <Tr>
              <Th>עובד/ת</Th>
              <Th>סניף</Th>
              <Th>חתם/ה</Th>
            </Tr>
          </thead>
          <tbody>
            {data.employees.map((row) => (
              <Tr key={row.employee_id}>
                <Td>{row.name}</Td>
                <Td>{row.branch ?? '—'}</Td>
                <Td>
                  {row.signed_at ? (
                    <span className="tabular">{formatDateTime(row.signed_at)}</span>
                  ) : (
                    <StatusBadge tone="bad">עוד לא</StatusBadge>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Panel>
  );
}

function DocumentForm({
  document,
  categories,
  saving,
  onSave,
  onCancel,
}: {
  document: Document | null;
  categories: { id: string; name: string }[];
  saving: boolean;
  onSave: (input: {
    title: string;
    categoryIds: string[];
    body: string | null;
    requiresSignature: boolean;
    isPublished: boolean;
  }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(document?.title ?? '');
  const [chosen, setChosen] = useState<string[]>(
    document?.categories.map((category) => category.id) ?? [],
  );
  const [body, setBody] = useState(document?.body ?? '');
  const [requiresSignature, setRequiresSignature] = useState(document?.requires_signature ?? false);
  const [isPublished, setIsPublished] = useState(document?.is_published ?? true);

  const contentChanged = document !== null && body !== (document.body ?? '');
  const confirm = useConfirm();

  /** מה שנשמר בפועל, אחרי שהוכרע אם צריך אישור לגרסה חדשה. */
  const save = () =>
    onSave({
      title: title.trim(),
      categoryIds: chosen,
      body: body.trim() || null,
      requiresSignature,
      isPublished,
    });

  return (
    <form
      className="grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim()) return;
        // שינוי תוכן של נוהל שדורש חתימה מבטל את כל החתימות
        // הקיימות ומחייב את כולם לחתום שוב. זו לא פעולת עריכה
        // רגילה, ולכן היא נעצרת לשאלה.
        if (contentChanged && requiresSignature) {
          confirm.ask({
            title: 'גרסה חדשה לנוהל',
            tone: 'warn',
            body: (
              <p className="m-0">
                שינוי התוכן יעלה גרסה. כל מי שחתם על הנוהל יתבקש לחתום עליו מחדש, והחתימות הקודמות
                כבר לא יהיו בתוקף.
              </p>
            ),
            confirmLabel: 'שמירה כגרסה חדשה',
            onConfirm: save,
          });
          return;
        }
        save();
      }}
    >
      <div className="grid gap-1">
        <label htmlFor="title" className="text-body-sm text-muted">
          כותרת
        </label>
        <input
          id="title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          className={FIELD}
        />
      </div>

      {/*
        בחירה מרובה ולא קטגוריה אחת: נוהל בטיחות אש הוא גם של
        החנות וגם של המחסן, ושדה יחיד הכריח לשכפל את הנוהל — ואז
        שתי רשימות חתימות על אותו נוסח.
      */}
      <fieldset className="m-0 grid gap-2 border-0 p-0">
        <legend className="p-0 text-body-sm text-muted">
          לאן זה שייך <span className="text-muted">אפשר לבחור כמה</span>
        </legend>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const isOn = chosen.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={isOn}
                onClick={() =>
                  setChosen(
                    isOn ? chosen.filter((id) => id !== category.id) : [...chosen, category.id],
                  )
                }
                className={cn(
                  'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
                  isOn
                    ? 'border-ink bg-olive font-semibold text-ink'
                    : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                )}
              >
                {isOn && <Check size={16} strokeWidth={2.5} aria-hidden="true" />}
                {category.name}
              </button>
            );
          })}
        </div>

        {chosen.length === 0 && (
          <p className="m-0 text-label text-muted">
            בלי קטגוריה הנוהל יישב בסוף הרשימה, בבלוק "בלי קטגוריה".
          </p>
        )}
      </fieldset>

      <div className="grid gap-1">
        <label htmlFor="body" className="text-body-sm text-muted">
          תוכן הנוהל
        </label>
        <textarea
          id="body"
          rows={10}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className={`${FIELD} py-2`}
        />
      </div>

      <label className="flex items-center gap-2 text-body">
        <input
          type="checkbox"
          checked={requiresSignature}
          onChange={(event) => setRequiresSignature(event.target.checked)}
          className="size-5"
        />
        דורש קריאה וחתימה
      </label>

      <label className="flex items-center gap-2 text-body">
        <input
          type="checkbox"
          checked={isPublished}
          onChange={(event) => setIsPublished(event.target.checked)}
          className="size-5"
        />
        מפורסם לעובדים
      </label>

      {contentChanged && requiresSignature && (
        <p className="m-0 rounded-sm border border-warn p-2 text-body-sm">
          שינית את התוכן. השמירה תעלה את הגרסה ל {(document?.version ?? 1) + 1}, וכל העובדים יתבקשו
          לחתום מחדש.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" type="submit" disabled={saving}>
          שמירת הנוהל
        </Button>
        <Button onClick={onCancel}>ביטול</Button>
      </div>
      {confirm.dialog}
    </form>
  );
}
